import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  Stack,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { EditOutlined } from "@mui/icons-material";
import { styledClass } from "../../utils/constants";
export default function UserManagementList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get page from query params, default to 1
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);

  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(pageFromQuery);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  // Sync currentPage with query param
  useEffect(() => {
    if (currentPage !== pageFromQuery) {
      setCurrentPage(pageFromQuery);
    }
    // eslint-disable-next-line
  }, [pageFromQuery]);

  // Update query param when currentPage changes
  useEffect(() => {
    if (currentPage !== pageFromQuery) {
      setSearchParams({ page: currentPage });
    }
    // eslint-disable-next-line
  }, [currentPage]);

  const fetchUsers = async (query) => {
    const body = {
      search: query || "",
      page: currentPage,
      limit: pageSize,
      status: status,
    };

      setIsLoading(true);
      setLoaderDelay(false);
      
      await userServices.getUser(body).then((res) => {
        if (res.data.data.users.length >= 0) {
          setData(res?.data?.data?.users);
          setTotalPages(res?.data?.data?.pagination?.total);
          setTotalRows(res?.data?.data?.pagination?.total);
        }
        setIsLoading(false);
        setLoaderDelay(true);
     
    } ).catch((err) => {
      setData([]);
      errorHandler(err);
      setIsLoading(false);
      setLoaderDelay(false);
    })
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  // Remove localStorage logic

  // Handle page change: update state and query param
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page });
  };

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await userServices.deleteUser({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const BlockData = async (id) => {
    const userData = data.find((item) => item._id === id);
    Swal.fire({
      text: `Are you sure you want to ${
        userData?.isLocked ? "unblock this user" : "block this user"
      }?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await userServices.blockUser({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === true ? false : true;
      const res = await userServices.changeStatus({
        id,
        isActive: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message);
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px",
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => row.name,
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Phone Number",
      sortable: true,
      selector: (row) => row.phone,
    },
    {
      status: "status",
      name: "Status",
      width: "180px",
      // sortable: true,
      selector: (row) => row.status,
      cell: (row) => {
        const status = row.status?.toLowerCase();
        const statusColorMap = {
          pending: "warning",
          approved: "info",
          rejected: "error",
          active: "success",
          inactive: "default",
        };
        const customStyles = {
          active: {
            backgroundColor: "#2e7d32",
            color: "#fff",
            border: "1px solid #1b5e20",
          },
          pending: {
            backgroundColor: "#f9a825",
            color: "#fff",
            border: "1px solid #f57f17",
          },
          approved: {
            backgroundColor: "#0288d1",
            color: "#fff",
            border: "1px solid #01579b",
          },
          rejected: {
            backgroundColor: "#c62828",
            color: "#fff",
            border: "1px solid #b71c1c",
          },
          inactive: {
            backgroundColor: "#9e9e9e",
            color: "#fff",
            border: "1px solid #757575",
          },
        };
        return (
          <Box display="flex" flexDirection="column">
            <Chip
              label={status?.charAt(0).toUpperCase() + status?.slice(1)}
              sx={{
                mt: 0.5,
                width: "fit-content",
                ...(customStyles[status] || {}),
              }}
              size="small"
            />
          </Box>
        );
      },
    },
    {
      name: "Action",
      center: true,
      width: "200px",
      cell: (row) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Tooltip title="View" placement="top">
            <IconButton
              sx={styledClass("viewIcon")}
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
                navigate(`/user-management/view/${row._id}?page=${currentPage}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="top">
            <IconButton
              sx={styledClass('edit')}
              onClick={() => {
                navigate(`/user-management/view/${row?._id}?page=${currentPage}`, {
                  state: "edit",
                });
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title={row?.isLocked ? "Unblock" : "Block"} placement="top">
            <IconButton
              sx={styledClass(row?.isLocked ? "unlockIcon" : "lockIcon")}
              onClick={() => BlockData(row._id)}
            >
              {row?.isLocked ? (
                <LockOpenIcon fontSize="small" />
              ) : (
                <LockIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton
              sx={styledClass("deleteIcon")}
              onClick={() => deleteData(row._id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Card>
      <title> User Management</title>
      <CardHeader title="User Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          className="filter_option_css"
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              {searchItem && (
                <IconButton
                  aria-label="clear-search"
                  onClick={() => {
                    setSearchItem("");
                    fetchUsers("");
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </InputAdornment>
          }
        />
      </Box>
      <CardContent>
      {isLoading ? <Loader /> : 
      data && data.length > 0 ?  
      <DataTable      
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          paginationDefaultPage={currentPage}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        /> : 
        (<Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
            <p style={{ textAlign: 'center', margin: 0 }}>No Data Found</p>
            </Box>)}
      </CardContent>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
